import { useState, useCallback, useEffect, useRef } from 'react';
import { cartApi, ValidatedCartResponse } from '@/api/customer/cart';
import { cartCache } from '@/lib/cartCache';
import { useCartStore, CartItem } from '@/features/user/stores/useCartStore';
import { useToast } from '@/components/shared/ui/use-toast';

export interface CachedCartData extends ValidatedCartResponse {
  cachedAt: number;
}

interface ValidationOptions {
  enabled?: boolean;
  debounceMs?: number;
  destination?: {
    state: string;
    postalCode: string;
  };
}

export const useCartValidation = (options: ValidationOptions = {}) => {
  const { enabled = true, debounceMs = 800 } = options;

  // Select only what we need individually to prevent object recreation
  const items = useCartStore(s => s.state.items);
  const { toast } = useToast();

  const [validatedCart, setValidatedCart] = useState<CachedCartData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFromCache, setIsFromCache] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isValidatingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Stabilize destination values to avoid object reference changes
  const destState = options.destination?.state;
  const destPostalCode = options.destination?.postalCode;

  const validateWithBackend = useCallback(async (currentItems: CartItem[], signal?: AbortSignal): Promise<CachedCartData | null> => {
    // We allow concurrent calls if they have different signals (managed by useEffect)
    // but we use isValidatingRef to prevent accidental double-triggers from the same cycle
    if (isValidatingRef.current && !signal) return null;

    isValidatingRef.current = true;
    try {
      const payload = {
        items: currentItems.map(item => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity,
          attributes: item.attributes
        })),
        destination: destState && destPostalCode ? {
          state: destState,
          postalCode: destPostalCode
        } : undefined
      };

      console.log('🔄 Validating cart with backend (autoritative)...');
      // Pass the signal to the API call
      const response = await cartApi.validate(payload);

      // If signal was aborted, the API client should have thrown, 
      // but we check anyway just in case of non-standard client behavior
      if (signal?.aborted) return null;

      const storeUpdates: {id: string, quantity: number}[] = [];
      let itemsWereAdjusted = false;

      // Plan stock adjustments
      response.items.forEach((vItem) => {
        const originalItem = currentItems.find(i =>
          i.productId === vItem.productId && (i.variantId || '') === (vItem.variantId || '')
        );

        if (originalItem && originalItem.quantity !== vItem.quantity) {
          itemsWereAdjusted = true;
          storeUpdates.push({ id: originalItem.id, quantity: vItem.quantity });
          
          toast({
            title: "Stock ajustado",
            description: `Se ajustó la cantidad de ${vItem.name || 'un producto'} a ${vItem.quantity} por falta de stock.`,
            variant: "warning"
          });
        }
      });

      const cacheData: CachedCartData = {
        ...response,
        cachedAt: Date.now()
      };

      // Perform bulk update if needed
      if (itemsWereAdjusted) {
        cartCache.set(response.items, cacheData);
        useCartStore.getState().bulkUpdateItems(storeUpdates);
      } else {
        cartCache.set(currentItems, cacheData);
      }

      return cacheData;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('🛑 Validation aborted');
        return null;
      }
      console.error('❌ Validation error:', err);
      throw err;
    } finally {
      isValidatingRef.current = false;
    }
  }, [destState, destPostalCode, toast]);

  // Stabilize the revalidate function
  const revalidate = useCallback(() => {
    return validateWithBackend(items);
  }, [validateWithBackend, items]);

  // Initial load and periodic validation
  useEffect(() => {
    if (!enabled || items.length === 0) {
      setValidatedCart(null);
      return;
    }

    let isMounted = true;
    
    // Cancel any previous pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Activar estado de carga inmediatamente si no estamos usando un cache válido
    if (isMounted && !cartCache.get(items)) {
      setIsLoading(true);
    }

    const runValidation = async () => {
      // 1. Try cache first
      const cached = cartCache.get(items);
      if (cached) {
        console.log('📦 Using cached validation results');
        if (isMounted) {
          setValidatedCart(cached);
          setIsFromCache(true);
          setIsLoading(false); // Make sure to clear loading if cache hit
        }

        // If cache is older than 5 minutes, revalidate in background
        if (Date.now() - cached.cachedAt < 5 * 60 * 1000) {
          return;
        }
      }

      // 2. Validate with backend
      if (isMounted) setIsLoading(true);

      try {
        const result = await validateWithBackend(items, signal);
        if (isMounted && result) {
          setValidatedCart(result);
          setIsFromCache(false);
          setError(null);
        }
      } catch (err) {
        if (isMounted && !(err instanceof Error && err.name === 'AbortError')) {
          setError(err instanceof Error ? err : new Error('Validation failed'));
        }
      } finally {
        if (isMounted && !signal.aborted) setIsLoading(false);
      }
    };

    const timer = setTimeout(runValidation, debounceMs);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [items, enabled, debounceMs, validateWithBackend]);

  return {
    validatedCart,
    isLoading,
    isFromCache,
    error,
    revalidate
  };
};
